import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Star, MessageSquare } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { useSiteSettings } from '../hooks/useSiteSettings';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

export const RecensioniPage = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const { settings } = useSiteSettings();
  const [formData, setFormData] = useState({
    name: '',
    rating: 5,
    comment: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const response = await axios.get(`${API}/reviews`);
        setReviews(response.data);
      } catch (error) {
        console.error('Error fetching reviews:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchReviews();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.comment) {
      toast.error('Per favore compila tutti i campi');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/reviews`, formData);
      toast.success('Grazie per la tua recensione! Sarà pubblicata dopo l\'approvazione.');
      setFormData({ name: '', rating: 5, comment: '' });
    } catch (error) {
      console.error('Error submitting review:', error);
      toast.error('Errore nell\'invio della recensione. Riprova più tardi.');
    } finally {
      setSubmitting(false);
    }
  };

  const StarRating = ({ rating, onRate, interactive = false }) => {
    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type={interactive ? 'button' : undefined}
            onClick={interactive ? () => onRate(star) : undefined}
            className={interactive ? 'cursor-pointer' : 'cursor-default'}
            disabled={!interactive}
          >
            <Star
              className={`w-6 h-6 ${
                star <= rating
                  ? 'fill-accent text-accent'
                  : 'fill-transparent text-gray-300'
              }`}
            />
          </button>
        ))}
      </div>
    );
  };

  return (
    <div data-testid="recensioni-page">
      {/* Hero */}
      <section className="relative flex items-center" data-testid="reviews-hero">
        <div className="absolute inset-0">
          {settings.recensioni_hero_image_url ? (
            <img
              src={settings.recensioni_hero_image_url}
              alt="Recensioni"
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-b from-primary-dark to-primary" />
          )}
          <div className="absolute inset-0 hero-overlay" />
        </div>
        
        <div className="relative container-custom pt-24 pb-6 md:pt-28 md:pb-8">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="text-accent font-body text-xs uppercase tracking-widest mb-1 block">
              La Voce dei Clienti
            </span>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-white max-w-3xl">
              Recensioni
            </h1>
            <p className="font-body text-sm text-white/80 mt-2 max-w-xl">
              Scopri cosa dicono i nostri clienti sui prodotti e sulla nostra azienda.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Reviews List */}
      <section className="section-spacing" data-testid="reviews-list">
        <div className="container-custom">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3].map((n) => (
                <div key={n} className="animate-pulse p-6 bg-background-paper rounded-xl">
                  <div className="h-4 bg-white rounded w-1/3 mb-4" />
                  <div className="h-4 bg-white rounded w-full mb-2" />
                  <div className="h-4 bg-white rounded w-3/4" />
                </div>
              ))}
            </div>
          ) : reviews.length === 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center py-16 bg-background-paper rounded-2xl"
            >
              <MessageSquare className="w-16 h-16 text-primary/30 mx-auto mb-6" />
              <h2 className="font-heading text-2xl font-semibold text-text-DEFAULT mb-4">
                Ancora nessuna recensione
              </h2>
              <p className="font-body text-text-muted max-w-md mx-auto mb-8">
                Sii il primo a lasciare una recensione! La tua opinione è importante per noi.
              </p>
            </motion.div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {reviews.map((review, index) => (
                <motion.div
                  key={review.id}
                  initial={{ opacity: 0, y: 40 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="p-8 bg-white rounded-2xl border border-primary/10 shadow-card"
                  data-testid={`review-${review.id}`}
                >
                  <StarRating rating={review.rating} />
                  <p className="font-body text-text-muted leading-relaxed mt-4 mb-6">
                    "{review.comment}"
                  </p>
                  <p className="font-body font-medium text-text-DEFAULT">
                    — {review.name}
                  </p>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Leave Review Form */}
      <section className="section-spacing bg-background-paper" data-testid="review-form-section">
        <div className="container-custom">
          <div className="max-w-2xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <span className="text-secondary font-body text-sm uppercase tracking-widest mb-4 block">
                Condividi la Tua Esperienza
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-text-DEFAULT">
                Lascia una Recensione
              </h2>
            </motion.div>

            <motion.form
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              onSubmit={handleSubmit}
              className="bg-white p-8 md:p-10 rounded-2xl border border-primary/10 shadow-card"
              data-testid="review-form"
            >
              <div className="space-y-6">
                <div>
                  <Label htmlFor="name" className="font-body text-text-DEFAULT mb-2 block">
                    Nome *
                  </Label>
                  <Input
                    id="name"
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Il tuo nome"
                    className="w-full rounded-xl border-primary/20 focus:border-primary focus:ring-primary"
                    data-testid="review-name-input"
                  />
                </div>

                <div>
                  <Label className="font-body text-text-DEFAULT mb-3 block">
                    Valutazione *
                  </Label>
                  <StarRating
                    rating={formData.rating}
                    onRate={(rating) => setFormData({ ...formData, rating })}
                    interactive
                  />
                </div>

                <div>
                  <Label htmlFor="comment" className="font-body text-text-DEFAULT mb-2 block">
                    La tua recensione *
                  </Label>
                  <Textarea
                    id="comment"
                    value={formData.comment}
                    onChange={(e) => setFormData({ ...formData, comment: e.target.value })}
                    placeholder="Racconta la tua esperienza con i nostri prodotti..."
                    rows={5}
                    className="w-full rounded-xl border-primary/20 focus:border-primary focus:ring-primary resize-none"
                    data-testid="review-comment-input"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-primary hover:bg-primary-dark text-white rounded-full py-6 font-body font-medium btn-primary"
                  data-testid="review-submit-btn"
                >
                  {submitting ? 'Invio in corso...' : 'Invia Recensione'}
                </Button>
              </div>
            </motion.form>
          </div>
        </div>
      </section>
    </div>
  );
};
